import { useLocation, useNavigate } from "@tanstack/react-router";
import { createContext, useContext, useLayoutEffect, useMemo, useState } from "react";

import { ErrorHandler } from "@/web/lib/errors";
import {
  useAdminSmbData,
  useSuspendedBrandsForUser,
} from "@/web/routes/_protected/smb/-dashboard/dashboardData";

const STORAGE_KEY = "tycho.selectedBrandId";

/**
 * Search-param name carrying the selected brand as an INDEX into the user's
 * brand list (`?brand=1`), gmail-style. See `brandIndexSearch`.
 */
export const BRAND_SEARCH_PARAM = "brand";

/**
 * Parse `?brand=N` out of raw search params. Exported so routes that are
 * brand-scoped can reuse the exact same `validateSearch` shape rather than
 * hand-rolling a second parser that could disagree about, say, whether `"0"` is
 * valid.
 *
 * Deliberately lenient: anything unparseable becomes `undefined` (= "brand 0"),
 * which is the same thing a missing param means. A junk URL should land the
 * user on their first brand, not on an error page.
 */
export function brandIndexSearch(search: Record<string, unknown>): { brand?: number } {
  const raw = search[BRAND_SEARCH_PARAM];
  // Only number and string are meaningful here; anything else (object, array
  // from a repeated `?brand=`) is junk and falls through to the default below.
  const parsed =
    typeof raw === "number" ? raw : typeof raw === "string" ? Number.parseInt(raw, 10) : Number.NaN;
  // Index 0 is the default and is represented by an ABSENT param, so it is
  // normalized away here too — otherwise `?brand=0` and no-param would be two
  // URLs for one state and the cleanup effect would fight itself.
  return Number.isInteger(parsed) && parsed > 0 ? { brand: parsed } : {};
}

type BrandSelectionValue = {
  /** The currently-selected brand id (as a string), reconciled against the
   * user's live brand list. Always a real, owned brand id when the user has any
   * brands. */
  selectedBrandId: string;
  /** Persist a new selection. Ignores ids the user doesn't belong to. */
  setSelectedBrandId: (id: string) => void;
  /** Every brand id (string) the user belongs to, in view order. */
  brandIds: readonly string[];
  /**
   * The selected brand's INDEX in `brandIds`, for building links that carry the
   * selection across routes (`?brand=1`). `undefined` for the first brand, which
   * is the no-param default — so a single-brand user's URLs never grow a param.
   */
  selectedBrandIndex: number | undefined;
};

const BrandSelectionContext = createContext<BrandSelectionValue | null>(null);

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    // Private mode / storage disabled — fall back to in-memory selection.
    ErrorHandler.reportError(error);
    return null;
  }
}

function writeStored(id: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch (error) {
    // Private mode / storage disabled — selection still works in-memory.
    ErrorHandler.reportError(error);
  }
}

/**
 * Brand-selection provider for the SMB dashboard. Backs the brand switcher and
 * every downstream "which brand am I looking at" read.
 *
 * The dashboard's live views (`all_brands_for_user`, `smb_super_view`, …)
 * already return ALL the user's brands; this provider just picks which one the
 * dashboard renders, filtered client-side. For a single-brand user it resolves
 * to that sole brand and the switcher renders nothing — guaranteeing the
 * status-quo dashboard is byte-for-byte unchanged (see docs/MULTI_USER_BRANDS.md
 * §2.1).
 *
 * The selection lives in the URL as `?brand=<index>` (gmail-style), with
 * localStorage as the fallback for a URL that doesn't carry one. Precedence is
 * deliberate:
 *
 *   1. `?brand=N` in the URL — an EXPLICIT, shareable, per-tab statement of
 *      which business is being looked at. It wins, so a link pasted to a
 *      colleague (or opened in a second tab) shows the brand it names rather
 *      than whatever this browser last selected.
 *   2. localStorage — restores the last selection when the URL is bare, which is
 *      what makes a plain visit to `/profile/smb` resume where you left off.
 *   3. The first brand.
 *
 * Why an index and not the brand UUID: the URL stays short and readable, and it
 * doesn't publish an internal identifier into browser history, bookmarks, or
 * referrer headers. The cost is that an index is only meaningful against the
 * user's own brand list — which is exactly the scope this param has.
 *
 * The reconciliation runs against the live brand list on every render, so an
 * index or stored id that no longer resolves (brand removed, someone else's
 * link, `?brand=99`) falls back to the first brand rather than erroring.
 */
export function BrandSelectionProvider({ children }: { children: React.ReactNode }) {
  const brands = useSuspendedBrandsForUser();
  const brandIds = useMemo(() => brands.map((b) => b.id.toString()), [brands]);

  // Inside the admin SMB viewer the "brands" list is the single injected target
  // brand, and the admin is not choosing their own business — so we must NOT
  // touch the shared localStorage key (it would clobber the admin's own
  // dashboard selection) and must not read or write the URL (the admin viewer
  // has its own route + its own `?tab=` params). In that mode the selection is
  // purely in-memory and always resolves to the injected brand.
  const isAdminSnapshot = useAdminSmbData() !== null;

  const navigate = useNavigate();
  // `useLocation` rather than a route-specific `Route.useSearch()`: this
  // provider is mounted on both `/profile/smb` and the admin viewer route, so it
  // can't bind to one route's search shape.
  const search = useLocation({ select: (l) => l.search as Record<string, unknown> });
  const urlBrandIndex = isAdminSnapshot ? undefined : brandIndexSearch(search).brand;

  const [selected, setSelected] = useState<string | null>(() =>
    isAdminSnapshot ? null : readStored(),
  );

  // Reconcile, in precedence order (see the doc comment). Derived rather than
  // stored in state so it always tracks the live brand list — and the URL —
  // without an extra render.
  const fromUrl = urlBrandIndex !== undefined ? brandIds[urlBrandIndex] : undefined;
  const fromStorage = selected && brandIds.includes(selected) ? selected : undefined;
  const effective = fromUrl ?? fromStorage ?? brandIds[0] ?? "";

  const selectedBrandIndex = useMemo(() => {
    const index = brandIds.indexOf(effective);
    // Index 0 is the no-param default; see `brandIndexSearch`.
    return index > 0 ? index : undefined;
  }, [brandIds, effective]);

  // Mirror the reconciled value back into memory + localStorage so a later mount
  // (or a bare URL) starts from a valid id. Never in the admin viewer.
  useLayoutEffect(() => {
    if (effective && effective !== selected) {
      setSelected(effective);
      if (!isAdminSnapshot) writeStored(effective);
    }
  }, [effective, selected, isAdminSnapshot]);

  // Drop a `?brand=N` that doesn't resolve to a real brand, so the address bar
  // can't keep asserting a selection the app isn't honoring. Without this, a
  // stale bookmark (`?brand=3` after a brand was removed) would silently render
  // brand 0 while the URL still claimed brand 3 — and copying that URL would
  // propagate the lie. `replace` so it doesn't add a history entry the user has
  // to press Back through.
  useLayoutEffect(() => {
    if (isAdminSnapshot) return;
    if (urlBrandIndex === undefined) return;
    if (fromUrl !== undefined) return;
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, [BRAND_SEARCH_PARAM]: undefined }),
      replace: true,
    });
  }, [isAdminSnapshot, urlBrandIndex, fromUrl, navigate]);

  const value = useMemo<BrandSelectionValue>(
    () => ({
      selectedBrandId: effective,
      setSelectedBrandId: (id: string) => {
        const index = brandIds.indexOf(id);
        if (index < 0) return;
        setSelected(id);
        if (isAdminSnapshot) return;
        writeStored(id);
        // Push the selection into the URL so it survives reload, Back, and
        // link-sharing. Index 0 clears the param rather than writing `?brand=0`,
        // keeping the single-brand (and first-brand) URL clean — the
        // no-regression constraint in docs/MULTI_USER_BRANDS.md §2.1.
        void navigate({
          to: ".",
          search: (prev: Record<string, unknown>) => ({
            ...prev,
            [BRAND_SEARCH_PARAM]: index > 0 ? index : undefined,
          }),
          replace: true,
        });
      },
      brandIds,
      selectedBrandIndex,
    }),
    [effective, brandIds, isAdminSnapshot, navigate, selectedBrandIndex],
  );

  return <BrandSelectionContext value={value}>{children}</BrandSelectionContext>;
}

/**
 * Read the brand-selection context. Throws if used outside the provider so a
 * missing mount is caught in dev rather than silently defaulting.
 */
export function useBrandSelection(): BrandSelectionValue {
  const ctx = useContext(BrandSelectionContext);
  if (!ctx) {
    throw new Error("useBrandSelection must be used within a BrandSelectionProvider");
  }
  return ctx;
}

/** Convenience: just the selected brand id string. */
export function useSelectedBrandId(): string {
  return useBrandSelection().selectedBrandId;
}

/**
 * The selected brand's index, for threading the selection through a navigation
 * (`search: { brand: useSelectedBrandIndex() }`). `undefined` on the first brand
 * so the param is simply omitted.
 */
export function useSelectedBrandIndex(): number | undefined {
  return useBrandSelection().selectedBrandIndex;
}

/**
 * The full selected brand row (or the first brand as a fallback), resolved
 * against the live brand list. Use this when you need the brand's `Uuid` id
 * object (e.g. to pass to a view filter that compares Uuids) rather than the
 * string form. Returns `null` only if the user has no brands.
 */
export function useSelectedBrand() {
  const brands = useSuspendedBrandsForUser();
  const { selectedBrandId } = useBrandSelection();
  return useMemo(
    () => brands.find((b) => b.id.toString() === selectedBrandId) ?? brands[0] ?? null,
    [brands, selectedBrandId],
  );
}
